/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM 
export enum FilterHeaderMode {
	Filters = 'Filters',
	Entity = 'Entity',
	EntityFilters = 'EntityFilters',
	Hidden = 'Hidden',
}

export enum FilterPersistenceMode {
	None = 'None',
	Session = 'Session',
	Local = 'Local',
}

export enum FilterApplyMode {
	Immediate = 'Immediate',
	Debounced = 'Debounced',
}

export interface FilterHeaderOptions {
	mode: FilterHeaderMode;
	showHeader: boolean;
	showActiveFiltersBadge: boolean;
	showResultSummary: boolean;
	showClearFilters: boolean;
	showPersistenceToggle: boolean;
	showExpandCollapse: boolean;
	allowCollapse: boolean;
	startExpanded: boolean;
	collapseOnMobile: boolean;
}

export const DEFAULT_FILTER_HEADER_OPTIONS: FilterHeaderOptions = {
	mode: FilterHeaderMode.Filters,
	showHeader: true,
	showActiveFiltersBadge: true,
	showResultSummary: true,
	showClearFilters: true,
	showPersistenceToggle: false,
	showExpandCollapse: true,
	allowCollapse: true,
	startExpanded: true,
	collapseOnMobile: true,
};

export interface FilterPersistenceOptions {
	enabled: boolean;
	mode: FilterPersistenceMode;
	storageKey: string;
	schemaVersion: number;
	persistExpandedState: boolean;
}

export interface FilterLayoutOptions {
	desktopColumns: number;
	tabletColumns: number;
	mobileColumns: number;
}

export interface FilterFieldMetadata<TValue = unknown> {
	id: string;
	controlNames: string[];
	label: string;
	defaultValue: TValue;
	isActive: (value: TValue, defaultValue: TValue) => boolean;
	formatDisplayValue: (value: TValue) => string;
	validateRestoredValue?: (value: TValue) => boolean;
	applyMode: FilterApplyMode;
	debounceMs?: number;
	removable?: boolean;
	persist?: boolean;
	sensitive?: boolean;
}

export interface ActiveFilterLozenge {
	id: string;
	label: string;
	formattedValue: string;
	removable: boolean;
	accessibleRemoveLabel: string;
}

export interface PersistedFilterState {
	schemaVersion: number;
	values: Record<string, unknown>;
	expanded?: boolean;
}

export const DEFAULT_FILTER_LAYOUT_OPTIONS: FilterLayoutOptions = {
	desktopColumns: 12,
	tabletColumns: 12,
	mobileColumns: 12,
};

export function valuesEqual(left: unknown, right: unknown): boolean {
	if (Object.is(left, right)) return true;
	if (left instanceof Date && right instanceof Date) return left.getTime() === right.getTime();
	if (Array.isArray(left) && Array.isArray(right)) return left.length === right.length && left.every((value, index) => valuesEqual(value, right[index]));
	if (left == null || right == null || typeof left !== 'object' || typeof right !== 'object') return false;
	const leftRecord = left as Record<string, unknown>;
	const rightRecord = right as Record<string, unknown>;
	const leftKeys = Object.keys(leftRecord).sort();
	const rightKeys = Object.keys(rightRecord).sort();
	return leftKeys.length === rightKeys.length && leftKeys.every((key, index) => key === rightKeys[index] && valuesEqual(leftRecord[key], rightRecord[key]));
}

export function displayFilterValue(value: unknown): string {
	if (value == null || value === '') return '';
	if (Array.isArray(value)) return value.length === 1 ? String(value[0]) : `${value.length} selected`;
	if (value instanceof Date) return value.toLocaleDateString();
	if (typeof value === 'object') return Object.values(value as Record<string, unknown>).filter(item => item != null && item !== '').map(displayFilterValue).join(' - ');
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	return String(value);
}

export function filterField<TValue>(options: Omit<FilterFieldMetadata<TValue>, 'isActive' | 'formatDisplayValue'> & Partial<Pick<FilterFieldMetadata<TValue>, 'isActive' | 'formatDisplayValue'>>): FilterFieldMetadata<TValue> {
	return {
		...options,
		isActive: options.isActive ?? ((value, defaultValue) => !valuesEqual(value, defaultValue)),
		formatDisplayValue: options.formatDisplayValue ?? displayFilterValue,
	};
}

export interface TextFilterFieldOptions {
	id: string;
	label: string;
	defaultValue?: string;
	sensitive?: boolean;
	debounceMs?: number;
}

export function textFilterField(options: TextFilterFieldOptions): FilterFieldMetadata<string> {
	return filterField({
		id: options.id,
		controlNames: [options.id],
		label: options.label,
		defaultValue: options.defaultValue ?? '',
		applyMode: FilterApplyMode.Debounced,
		debounceMs: options.debounceMs ?? 300,
		persist: true,
		sensitive: options.sensitive ?? false,
		removable: true,
	});
}

export interface ImmediateFilterFieldOptions<TValue> {
	id: string;
	label: string;
	defaultValue: TValue;
	sensitive?: boolean;
	formatDisplayValue?: (value: TValue) => string;
}

export function immediateFilterField<TValue>(options: ImmediateFilterFieldOptions<TValue>): FilterFieldMetadata<TValue> {
	return filterField({
		id: options.id,
		controlNames: [options.id],
		label: options.label,
		defaultValue: options.defaultValue,
		applyMode: FilterApplyMode.Immediate,
		persist: true,
		sensitive: options.sensitive ?? false,
		removable: true,
		formatDisplayValue: options.formatDisplayValue,
	});
}

export interface DateRangeFilterFieldOptions {
	id: string;
	label: string;
	fromControlName: string;
	toControlName: string;
	fromDefault: FilterDateValue;
	toDefault: FilterDateValue;
}

export type FilterDateValue = Date | string | null | {
	format?: (pattern: string) => string;
	isValid?: () => boolean;
	toDate?: () => Date;
};

export function formatFilterDate(value: FilterDateValue): string {
	if (value == null || value === '') return '';
	if (typeof value === 'object' && !(value instanceof Date)) {
		if (typeof value.isValid === 'function' && !value.isValid()) return '';
		if (typeof value.format === 'function') return value.format('DD/MM/YYYY');
		if (typeof value.toDate === 'function') return formatNativeDate(value.toDate());
	}
	if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
		const [year, month, day] = value.split('-');
		return `${day}/${month}/${year}`;
	}
	return formatNativeDate(value as Date | string);
}

function formatNativeDate(value: Date | string): string {
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${day}/${month}/${year}`;
}

export function dateRangeFilterField(options: DateRangeFilterFieldOptions): FilterFieldMetadata<Record<string, FilterDateValue>> {
	return filterField({
		id: options.id,
		controlNames: [options.fromControlName, options.toControlName],
		label: options.label,
		defaultValue: { [options.fromControlName]: options.fromDefault, [options.toControlName]: options.toDefault },
		applyMode: FilterApplyMode.Immediate,
		persist: true,
		removable: true,
		formatDisplayValue: value => {
			const from = formatFilterDate(value[options.fromControlName]);
			const to = formatFilterDate(value[options.toControlName]);
			if (from && to) return `${from} – ${to}`;
			if (from) return `From ${from}`;
			if (to) return `To ${to}`;
			return '';
		},
	});
}
